
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import Icon from './Icon';
import { View } from '../App';
import { auth } from '../services/firebase';

interface UserProfileProps {
    setView: (view: View) => void;
    onOpenWallet: (view?: 'overview' | 'credits') => void;
    isAutoDownload: boolean;
    toggleAutoDownload: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ setView, onOpenWallet, isAutoDownload, toggleAutoDownload }) => {
    const { user, logout, isCloudConnected } = useAuth();
    const { t, language, setLanguage, languageLabels } = useLanguage();
    const { theme, toggleTheme } = useTheme();
    
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [imgError, setImgError] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Reset image error if user changes
    useEffect(() => {
        setImgError(false);
    }, [user?.picture]);

    if (!user) return null;

    const languages = Object.keys(languageLabels) as (keyof typeof languageLabels)[];

    return (
        <div id="tour-wallet" className="flex items-center gap-2 md:gap-4 relative" ref={dropdownRef}>
            {/* Split Credits Button (Desktop) */}
            <div className="hidden md:flex items-center bg-[#151515] dark:bg-[#1a1a1a] rounded-full p-1 border border-white/10 shadow-sm hover:border-white/20 transition-all">
                <button 
                    onClick={() => onOpenWallet('credits')} 
                    className="flex items-center gap-2 pl-2 pr-3 py-1.5 hover:bg-white/5 rounded-full transition-colors group"
                    title={t('btnViewDetails')}
                >
                    <div className="w-5 h-5 rounded-full bg-apple-gold/20 flex items-center justify-center text-apple-gold group-hover:scale-110 transition-transform">
                        <Icon name="sparkles" className="w-3 h-3 fill-current" />
                    </div>
                    <span className="text-sm font-bold text-amber-100">{user.swarnas}</span>
                </button>
                <div className="w-[1px] h-4 bg-white/10 mx-1"></div>
                <button 
                    onClick={() => onOpenWallet('overview')} 
                    className="px-4 py-1.5 bg-apple-gold text-black text-xs font-bold rounded-full hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-apple-gold/20"
                >
                    Recharge
                </button>
            </div>

            {/* Mobile Fallback (Simple Button) */}
            <button 
                onClick={() => onOpenWallet('overview')}
                className="md:hidden group flex items-center gap-2 bg-slate-50 dark:bg-white/5 pl-3 pr-1 py-1 rounded-full border border-black/5 dark:border-white/5 transition-all"
            >
                <Icon name="diamond" className="w-3.5 h-3.5 text-apple-gold"/>
                <span className="font-bold text-sm text-slate-900 dark:text-white">{user.swarnas}</span>
            </button>

            {/* User Identity - Dropdown Trigger */}
            <div 
                className="flex items-center gap-2 md:gap-3 cursor-pointer select-none"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                <div className="text-right hidden sm:block">
                    <div className="flex items-center justify-end gap-1.5">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white leading-tight">{user.name}</p>
                        <div title={isCloudConnected ? "Synced to Cloud" : "Offline / Not Synced"} className={`${isCloudConnected ? 'text-green-500' : 'text-amber-500'} transition-colors`}>
                            <Icon name={isCloudConnected ? 'cloud' : 'cloud-off'} className="w-3 h-3" />
                        </div>
                    </div>
                </div>
                <div className="relative group">
                    {!imgError && user.picture ? (
                        <img 
                            src={user.picture} 
                            alt={user.name} 
                            onError={() => setImgError(true)}
                            className={`w-8 h-8 md:w-10 md:h-10 rounded-full border border-black/5 dark:border-white/10 transition-all object-cover ${isDropdownOpen ? 'ring-2 ring-apple-gold ring-offset-2 ring-offset-white dark:ring-offset-black' : 'group-hover:opacity-80'}`} 
                        />
                    ) : (
                        <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full border border-black/5 dark:border-white/10 transition-all flex items-center justify-center bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-300 ${isDropdownOpen ? 'ring-2 ring-apple-gold ring-offset-2 ring-offset-white dark:ring-offset-black' : 'group-hover:opacity-80'}`}>
                            <Icon name="user" className="w-5 h-5 md:w-6 md:h-6" />
                        </div>
                    )}
                </div>
            </div>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <div className="fixed top-20 left-1/2 -translate-x-1/2 w-[90vw] max-w-sm md:absolute md:top-full md:right-0 md:left-auto md:translate-x-0 md:w-80 md:mt-4 apple-blur bg-white/95 dark:bg-[#1c1c1e]/95 rounded-2xl shadow-2xl border border-black/5 dark:border-white/10 overflow-hidden z-[100] animate-in fade-in slide-in-from-top-4 duration-300 origin-top-right">
                    
                    {/* Header Info */}
                    <div className="p-4 border-b border-black/5 dark:border-white/5 bg-slate-50/50 dark:bg-white/5">
                        <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">{t('signedInAs')}</p>
                        <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user.name}</p>
                        <p className="text-xs text-slate-400 truncate">{user.email}</p>
                        {auth.currentUser?.phoneNumber && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-mono mt-1 flex items-center gap-1">
                                <Icon name="phone" className="w-3 h-3" />
                                {auth.currentUser.phoneNumber}
                            </p>
                        )}
                    </div>

                    {/* Settings Section (Theme & Language) */}
                    <div className="p-3 border-b border-black/5 dark:border-white/5 bg-slate-50/30 dark:bg-white/5">
                        <p className="px-1 text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-2">Appearance & Region</p>
                        
                        {/* Theme Toggle */}
                        <div className="flex items-center justify-between p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/5 transition-colors cursor-pointer" onClick={toggleTheme}>
                             <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                                <Icon name={theme === 'dark' ? 'moon' : 'sun'} className="w-4 h-4" />
                                <span>{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
                             </div>
                             <div className={`w-9 h-5 rounded-full relative transition-colors ${theme === 'dark' ? 'bg-slate-700' : 'bg-slate-300'}`}>
                                 <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all shadow-sm ${theme === 'dark' ? 'left-5' : 'left-1'}`} />
                            </div>
                        </div>

                        {/* Language Selector */}
                        <div className="mt-2 p-2">
                             <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                                <Icon name="globe" className="w-4 h-4" />
                                <span>Language</span>
                             </div>
                             <div className="grid grid-cols-5 gap-1">
                                {languages.map((lang) => (
                                    <button
                                        key={lang}
                                        onClick={() => setLanguage(lang)}
                                        className={`py-1.5 rounded-lg text-[10px] font-bold uppercase transition-all border ${language === lang ? 'bg-apple-gold text-white border-transparent shadow-sm' : 'bg-white dark:bg-white/5 text-slate-500 border-black/5 dark:border-white/5 hover:bg-slate-50 dark:hover:bg-white/10'}`}
                                        title={languageLabels[lang]}
                                    >
                                        {lang}
                                    </button>
                                ))}
                             </div>
                        </div>
                    </div>
                    
                    {/* Navigation */}
                    <div className="p-2 space-y-1">
                        <button 
                            onClick={() => { setView('designer'); setIsDropdownOpen(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-500 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                <Icon name="diamond" className="w-4 h-4" />
                            </div>
                            {t('navDesigner')}
                        </button>

                        <button 
                            onClick={() => { onOpenWallet('overview'); setIsDropdownOpen(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-full bg-apple-gold/10 flex items-center justify-center text-apple-gold">
                                <Icon name="wallet" className="w-4 h-4" />
                            </div>
                            {t('navBilling')}
                        </button>

                        
                        {/* Feedback Link */}
                        <a
                            href="https://forms.gle/3vyCzzHLm8qDjxJu8"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 rounded-xl transition-colors group"
                            onClick={() => setIsDropdownOpen(false)}
                        >
                            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-500 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                                <Icon name="message" className="w-4 h-4" />
                            </div>
                            Feedback
                        </a>
                    </div>

                    {/* Auto Download Toggle */}
                    <div className="p-2 border-t border-black/5 dark:border-white/5">
                        <div 
                            className="flex items-center justify-between px-3 py-2 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 transition-colors cursor-pointer select-none" 
                            onClick={(e) => { e.stopPropagation(); toggleAutoDownload(); }}
                        >
                            <div className="flex items-center gap-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-500">
                                   <Icon name="download" className="w-4 h-4" />
                                </div>
                                {t('autoDownload')}
                            </div>
                            <div className={`w-9 h-5 rounded-full relative transition-colors ${isAutoDownload ? 'bg-apple-gold' : 'bg-slate-300 dark:bg-slate-600'}`}>
                                 <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all shadow-sm ${isAutoDownload ? 'left-5' : 'left-1'}`} />
                            </div>
                        </div>
                    </div>

                    {/* Logout */}
                    <div className="p-2 border-t border-black/5 dark:border-white/5">
                         <button 
                            onClick={() => { logout(); setIsDropdownOpen(false); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                        >
                            <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-500">
                                <Icon name="logout" className="w-4 h-4" />
                            </div>
                            {t('logoutButton')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserProfile;
